import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ButtonToolbar, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import "./HouseholdHeader.scss";
import type { Household } from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import type {
  EditableTask,
  EditableWorkflow,
} from "../../../../api/src/tasks/tasks.service";
import { NotificationContext } from "../../Notifications";
import ActionButton from "../../components/ActionButton";
import Avatar from "../../components/Avatar";
import MobileActionsMenu, {
  MobileActionItem,
} from "../../components/MobileActionsMenu";
import { NoteForm, NotesPane } from "../../components/Notes";
import CreateTaskPane from "../../components/CreateTaskPane";
import CreateWorkflowPane from "../../components/CreateWorkflowPane";
import FormFieldError from "../../components/FormFieldError";
import SubmitButton from "../../components/SubmitButton";
import SummaryPill from "../../components/SummaryPill";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import {
  useCreateHouseholdNote,
  useQueryHouseholdNotes,
} from "../../lib/notes";
import { formatCurrency } from "../../lib/numbers";
import { deserializeTask } from "../../tasks/lib";
import HouseholdArchiveDialog from "./HouseholdArchiveDialog";
import { HouseholdContext } from "./HouseholdInfo";
import { useCreateHouseholdWorkflow, useUnarchiveHousehold } from "./lib";
import { useToolbarOverflow } from "./useToolbarOverflow";

const schema: yup.ObjectSchema<{ name: string }> = yup.object({
  name: yup.string().required().label("Name"),
});

const HouseholdNameField = ({
  household,
  householdId,
}: {
  household?: Household;
  householdId?: string;
}) => {
  const {
    register,
    handleSubmit,
    getValues,
    setFocus,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<{ name: string }>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({ name: household?.name });
  }, [household?.name, reset]);

  const updateHouseholdName = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<HouseholdsController["updateHouseholdName"]>
    >
  >(`/v1/households/${householdId}/name`, async (name: string) => ({
    method: "PUT",
    body: JSON.stringify({ name }),
  }));

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const [enableNameField, setEnableNameField] = useState(false);

  const onSubmit = useMutation({
    mutationFn: async (name: string) => {
      try {
        await updateHouseholdName(name);
        setEnableNameField(false);
        queryClient.setQueryData(
          ["households", parseInt(householdId ?? ""), { includeCRM: true }],
          {
            data: { ...household, name },
          },
        );
        reset({ name });
        notificationContext.pushNotification({
          id: `household-${householdId}-name`,
          header: "Household Name Updated",
          body: `${name} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save household name", err);
        notificationContext.pushNotification({
          id: `household-${householdId}-name`,
          header: "Failed to Update Household Name",
          body: `${name} was not saved`,
          variant: "danger",
        });
      }
    },
  });

  const onSaveName = useCallback(
    (ev: React.MouseEvent) => {
      if (!enableNameField) {
        ev.preventDefault();
        setEnableNameField(true);
      }
    },
    [enableNameField],
  );

  // Focus the name field on toggle. This does not work if we call
  // it before the re-render that displays the input field, so we
  // have to watch it using the effect hook.
  useEffect(() => {
    if (enableNameField) {
      setFocus("name");
    }
  }, [enableNameField, setFocus]);

  return (
    <Form onSubmit={handleSubmit((data) => onSubmit.mutateAsync(data.name))}>
      <div className="d-flex align-items-center">
        <div style={{ maxWidth: 360 }}>
          {enableNameField ? null : (
            <span
              data-testid="household-name"
              className="fs-3 fw-semibold lh-1 text-wrap"
            >
              {getValues("name")}
            </span>
          )}
          <Form.Group controlId="form-name" hidden={!enableNameField}>
            <Form.Control {...register("name")} type="text" size="lg" />
            <FormFieldError field={errors.name} />
          </Form.Group>
        </div>
        <SubmitButton
          variant="icon"
          icon={enableNameField ? "/icons/save.svg" : "/icons/edit.svg"}
          label={enableNameField ? "Save" : "Edit"}
          type={enableNameField ? "submit" : "button"}
          onClick={onSaveName}
          isSubmitting={isSubmitting}
          className="ms-2 me-3"
        />
      </div>
    </Form>
  );
};

const HouseholdHeader = () => {
  const { household } = useContext(HouseholdContext);
  const { householdId: householdIdStr } = useParams();
  const householdId = parseInt(householdIdStr ?? "");
  const navigate = useNavigate();
  const linkedToWealthbox = household?.idExternal.wealthbox != null;
  const linkedToRedtail = household?.idExternal.redtail != null;
  const linkedToSlant = household?.idExternal.slant != null;
  const linkedToCrm = linkedToWealthbox || linkedToRedtail || linkedToSlant;
  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  const slantConnected = profileBody?.data.connections.slant === true;
  // Workflows are only implemented for Wealthbox and Redtail; Slant has no
  // workflow templates or create path, so hide the action for Slant-only links.
  const supportsWorkflows = linkedToWealthbox || linkedToRedtail;

  const queryClient = useQueryClient();

  // Track if toolbar has horizontal overflow for fade indicator
  const { containerRef: toolbarRef, hasOverflow } = useToolbarOverflow([
    linkedToCrm,
    household?.isActive,
  ]);

  const [isShowCreateTask, setIsShowCreateTask] = useState(false);
  const showCreateTask = useCallback(() => {
    setIsShowCreateTask(!isShowCreateTask);
  }, [isShowCreateTask]);

  const createTask = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<HouseholdsController["createTaskForHousehold"]>
    >
  >(`/v1/households/${householdId}/tasks`, async (data: EditableTask) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const onCreateTask = useCallback(
    async (data: EditableTask) => {
      const body = await createTask(data);
      if (body.data == null) {
        return null;
      } else {
        queryClient.setQueryData(
          [
            "households",
            householdId,
            { includeAccounts: true, includeCRM: true },
          ],
          {
            ...household,
            tasks: [...(household?.tasks ?? []), deserializeTask(body.data)],
          },
        );

        return deserializeTask(body.data);
      }
    },
    [createTask, queryClient, householdId, household],
  );

  const [isShowCreateWorkflow, setIsShowCreateWorkflow] = useState(false);
  const showCreateWorkflow = useCallback(() => {
    setIsShowCreateWorkflow(!isShowCreateWorkflow);
  }, [isShowCreateWorkflow]);

  const createWorkflow = useCreateHouseholdWorkflow(householdId);

  const onCreateWorkflow = useCallback(
    async (data: EditableWorkflow) => {
      const workflow = await createWorkflow.mutateAsync(data);

      if (workflow === null) {
        return null;
      } else {
        queryClient.setQueryData(
          [
            "households",
            householdId,
            { includeAccounts: true, includeCRM: true },
          ],
          {
            ...household,
            workflows: [...(household?.workflows ?? []), workflow],
          },
        );

        return workflow;
      }
    },
    [createWorkflow, queryClient, householdId, household],
  );

  const [showNotes, setShowNotes] = useState(false);
  const onShowNotes = useCallback(() => {
    setShowNotes(!showNotes);
  }, [showNotes]);

  const {
    isPending: isPendingNotes,
    isError: isErrorNotes,
    data: dataNotes,
  } = useQueryHouseholdNotes(householdId);

  const createNote = useCreateHouseholdNote(householdId);

  const onCreateNote = useCallback(
    async (data: NoteForm) => {
      return await createNote.mutateAsync(data);
    },
    [createNote],
  );

  const [showModal, setShowModal] = useState(false);
  const handleArchiveHouseholdClick = useCallback(() => {
    setShowModal(true);
  }, []);

  const { mutateAsync: unarchiveHousehold } =
    useUnarchiveHousehold(householdId);

  const notificationContext = useContext(NotificationContext);

  const handleUnarchiveHousehold = useCallback(async () => {
    try {
      await unarchiveHousehold();

      setShowModal(false);
      notificationContext.pushNotification({
        id: `household-${householdId}`,
        header: "Household Unarchived",
        body: `${household?.name} unarchived`,
        variant: "success",
      });
    } catch (ex) {
      console.error("Failed to unarchive household", ex);
      notificationContext.pushNotification({
        id: `household-${householdId}`,
        header: "Failed to Unarchive Household",
        body: `${household?.name} was not unarchived`,
        variant: "danger",
      });
    }
  }, [household?.name, householdId, notificationContext, unarchiveHousehold]);

  const handleRunRebalance = useCallback(() => {
    if (!household) {
      return;
    }

    const hasHouseholdModel = household.assignedModelId != null;
    const hasAccountModels = (household.accounts ?? []).some(
      (account) => account.assignedModelId != null,
    );

    if (!hasHouseholdModel && !hasAccountModels) {
      notificationContext.pushNotification({
        id: `household-${householdId}-rebalance-error`,
        header: "Cannot Run Trade Proposal",
        body: "Cannot run a trade proposal since there is not a model assigned to the household or the accounts within the household. Please assign models to the household or the accounts and try again.",
        variant: "danger",
      });
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.set("household_id", householdId.toString());
    navigate(`/rebalances/new?${searchParams.toString()}`);
  }, [household, householdId, navigate, notificationContext]);

  const mobileActions = useMemo(() => {
    const actions: MobileActionItem[] = [
      {
        label: "Create Report",
        icon: "/icons/bar-chart.svg",
        to: `/reports/batches/new?type=custom&household_id=${householdId}`,
      },
      {
        label: "Run Trade Proposal",
        icon: "/icons/save.svg",
        onClick: handleRunRebalance,
      },
    ];
    if (linkedToCrm) {
      actions.push(
        {
          label: "Create Task",
          icon: "/icons/tasks.svg",
          onClick: showCreateTask,
        },
        ...(supportsWorkflows
          ? [
              {
                label: "Create Workflow",
                icon: "/icons/flowchart.svg",
                onClick: showCreateWorkflow,
              },
            ]
          : []),
        {
          label: "Notes",
          icon: "/icons/folded-list.svg",
          onClick: onShowNotes,
        },
      );
    }
    actions.push(
      household?.isActive
        ? {
            label: "Archive",
            icon: "/icons/trash.svg",
            onClick: handleArchiveHouseholdClick,
          }
        : {
            label: "Unarchive",
            icon: "/icons/trash.svg",
            onClick: handleUnarchiveHousehold,
          },
    );
    return actions;
  }, [
    householdId,
    handleRunRebalance,
    linkedToCrm,
    supportsWorkflows,
    showCreateTask,
    showCreateWorkflow,
    onShowNotes,
    household?.isActive,
    handleArchiveHouseholdClick,
    handleUnarchiveHousehold,
  ]);

  return (
    <>
      <Row>
        <Col>
          <div className="d-flex justify-content-end mb-3 d-lg-none">
            <MobileActionsMenu actions={mobileActions} />
          </div>
          <div
            ref={toolbarRef}
            className={`d-none d-lg-block household-actions-scroll${hasOverflow ? " has-overflow" : ""}`}
            role="region"
            aria-label="Household actions"
          >
            <ButtonToolbar className="mb-3 justify-content-end gap-2">
              <ActionButton
                variant="secondary"
                label="Create Report"
                as={Link}
                icon="/icons/bar-chart.svg"
                to={`/reports/batches/new?type=custom&household_id=${householdId}`}
              />
              <ActionButton
                variant="secondary"
                label="Run Trade Proposal"
                icon="/icons/save.svg"
                type="button"
                onClick={handleRunRebalance}
              />
              {!linkedToCrm ? null : (
                <>
                  <ActionButton
                    variant="secondary"
                    icon="/icons/tasks.svg"
                    label="Create Task"
                    type="button"
                    onClick={showCreateTask}
                  />
                  {supportsWorkflows && (
                    <ActionButton
                      variant="secondary"
                      icon="/icons/flowchart.svg"
                      label="Create Workflow"
                      type="button"
                      onClick={showCreateWorkflow}
                    />
                  )}
                  <ActionButton
                    variant="secondary"
                    icon="/icons/folded-list.svg"
                    label="Notes"
                    type="button"
                    onClick={onShowNotes}
                  />
                </>
              )}
              {household?.isActive ? (
                <ActionButton
                  type="button"
                  label="Archive"
                  icon="/icons/trash.svg"
                  onClick={handleArchiveHouseholdClick}
                />
              ) : (
                <ActionButton
                  type="button"
                  label="Unarchive"
                  icon="/icons/trash.svg"
                  onClick={handleUnarchiveHousehold}
                />
              )}
            </ButtonToolbar>
          </div>
        </Col>
        <HouseholdArchiveDialog
          showModal={showModal}
          setShowModal={setShowModal}
          archiveHouseholdId={householdId}
          archiveHouseholdName={household?.name ?? ""}
        />
      </Row>
      <Row>
        <Col
          xs={12}
          className="d-flex align-items-center justify-content-between flex-wrap"
        >
          <div className="d-flex align-items-center flex-wrap mb-3">
            {!household?.image ? null : (
              <Avatar
                src={household?.image}
                alt="household avatar"
                className="me-2"
                width={64}
                height={64}
              />
            )}
            <HouseholdNameField
              household={household}
              householdId={householdIdStr}
            />
            {!linkedToWealthbox ? null : (
              <a
                href={`${process.env.REACT_APP_WEALTHBOX_APP_ROOT}/contacts/${household.idExternal.wealthbox}`}
                rel="noreferrer"
                target="_blank"
                className="me-2"
              >
                View in Wealthbox
              </a>
            )}
            {!linkedToSlant ||
            !household?.slantBookId ||
            !process.env.REACT_APP_SLANT_APP_ROOT ? null : (
              <a
                href={`${process.env.REACT_APP_SLANT_APP_ROOT}/${household.slantBookId}/clients/${household.idExternal.slant}`}
                rel="noreferrer"
                target="_blank"
                className="me-2"
              >
                View in Slant
              </a>
            )}
          </div>
          <div>
            {/*
              Service Tier (s-slant-client-tier-as-019ec6dd): shown for a
              Slant-linked household when the user is connected to Slant
              (`connections.slant` on GET /profile) — the tier label, or "—"
              when untiered. `Household` always carries `serviceTier` (`null`
              when there is no tier, APP-1648), so the key being present can
              no longer tell a Slant household from any other.
            */}
            {!slantConnected || !linkedToSlant ? null : (
              <SummaryPill
                label="Service Tier"
                value={household.serviceTier?.label ?? "—"}
                size="sm"
                className="me-3 mb-3"
              />
            )}
            <SummaryPill
              label="AUM"
              value={formatCurrency(household?.householdBalance ?? 0)}
              size="sm"
              className="me-3 mb-3"
            />
            <SummaryPill
              label="Cash*"
              value={`${formatCurrency(household?.cashBalance ?? 0)}`}
              size="sm"
            />
          </div>
        </Col>
      </Row>
      <CreateTaskPane
        show={isShowCreateTask}
        setShow={setIsShowCreateTask}
        onCreate={onCreateTask}
        crm={{
          wealthbox: linkedToWealthbox,
          redtail: linkedToRedtail,
          slant: linkedToSlant,
        }}
        record={{ householdId }}
      />
      <CreateWorkflowPane
        show={isShowCreateWorkflow}
        setShow={setIsShowCreateWorkflow}
        onCreate={onCreateWorkflow}
      />
      <NotesPane
        showNotes={showNotes}
        setShowNotes={setShowNotes}
        notes={dataNotes ?? []}
        onCreate={onCreateNote}
        isLoading={isPendingNotes}
        isError={isErrorNotes}
      />
    </>
  );
};

export default HouseholdHeader;
